import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  memo,
  Suspense,
} from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  InitialsAvatar,
  EditableTableCell,
  AcademicYearSelect,
} from "../../dashboard/widget/tableWidget";
import { filterStorageUtils } from "../../../services/filterStorageUtils";
import MasterActions from "../../dashboard/widget/MasterActions.js";
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Dropdown,
  Badge,
  Spinner,
  Pagination,
  OverlayTrigger,
  Popover,
  Form,
  InputGroup,
  Modal,
  Nav,
  Tab,
} from "react-bootstrap";
import { format } from "date-fns";
import { MasterFilter } from "../widget/MasterFilter.js";
import {
  FaRedo,
  FaFilter,
  FaSort,
  FaCog,
  FaExclamationTriangle,
  FaTimes,
} from "react-icons/fa";
import { FiSearch } from "react-icons/fi";
import "../../../assets/css/table.css";
import { useSharedState } from "../../../services/hostelConst.js";
import {
  getMethods,
  callToast,
  getMasterActionStatusFieldName,
  showErrorMessages,
  getDefaultColumns,
  getMasterActionStatusValue,
} from "../../../services/utilityFunctions";
import { getAcademicYearList } from "../../../services/Academic-Services.js";
import { pagePaths } from "../../../services/constant";
import { useGlobalState } from "../../../services/hostelConst";
import { getLocalStorageData } from "../../../services/axiosComponent.js";
import RegexPatterns from "../../../services/regex.js";
import { useSelector, useDispatch } from "react-redux";
import {
  openModal,
  closeModal,
} from "../../../services/redux/reducers/modalSlice";
import { getValueTransition } from "framer-motion";
import { Select } from "antd";

// CSS for animations - updated with student portal theme
const getStyles = (isStudentPortal = false) => `
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from { transform: translateY(-10px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
}

@keyframes gradientShift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

.slide-in {
  animation: slideIn 0.3s ease-out;
}

.pulse {
  animation: pulse 0.3s ease-in-out;
}

.shake-animation {
  animation: shake 0.5s ease-in-out;
}

.hover-scale {
  transition: transform 0.15s ease-in-out;
}

.hover-scale:hover {
  transform: scale(1.02);
}

.table-row {
  transition: background-color 0.2s ease;
}

.table-row:hover {
  background-color: ${
    isStudentPortal ? "rgba(251, 191, 36, 0.04)" : "rgba(13, 110, 253, 0.04)"
  };
}

.filter-badge {
  animation: fadeIn 0.3s ease-in-out;
  transition: all 0.2s ease;
}

.filter-badge:hover {
  transform: translateY(-2px);
  box-shadow: ${
    isStudentPortal
      ? "0 2px 4px rgba(251, 191, 36, 0.1)"
      : "0 2px 4px rgba(13, 110, 253, 0.1)"
  };
}

.table-container {
  scroll-behavior: smooth;
}

/* Student Portal Specific Styles */
${
  isStudentPortal
    ? `
.student-card {
  background: linear-gradient(135deg, #ffffff 0%, #fefcf3 100%);
  border: 1px solid rgba(251, 191, 36, 0.1);
  box-shadow: 0 8px 32px rgba(251, 191, 36, 0.08), 0 4px 16px rgba(245, 158, 11, 0.05);
  border-radius: 16px;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.student-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 48px rgba(251, 191, 36, 0.12), 0 8px 24px rgba(245, 158, 11, 0.08);
}

.student-card-header {
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.03) 0%, rgba(245, 158, 11, 0.02) 100%);
  border-bottom: 1px solid rgba(251, 191, 36, 0.1);
  border-radius: 16px 16px 0 0;
}

.student-btn-primary {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  border: none;
  color: white;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(251, 191, 36, 0.3);
}

.student-btn-primary:hover {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 16px rgba(251, 191, 36, 0.4);
}

.student-btn-light {
  background: rgba(251, 191, 36, 0.1);
  border: 1px solid rgba(251, 191, 36, 0.2);
  color: #92400e;
  transition: all 0.3s ease;
}

.student-btn-light:hover {
  background: rgba(251, 191, 36, 0.2);
  border-color: rgba(251, 191, 36, 0.3);
  color: #78350f;
}

.student-badge {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: white;
  border-radius: 12px;
  padding: 4px 12px;
  font-size: 0.75rem;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(251, 191, 36, 0.2);
}

.student-table-header {
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.05) 0%, rgba(245, 158, 11, 0.03) 100%);
  border-bottom: 2px solid rgba(251, 191, 36, 0.1);
}

.student-table-header th {
  color: #92400e;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.student-pagination .page-item.active .page-link {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  border-color: #fbbf24;
  color: white;
}

.student-search-container {
  border: 1px solid rgba(251, 191, 36, 0.2);
  border-radius: 12px;
  background: rgba(251, 191, 36, 0.05);
  transition: all 0.3s ease;
}

.student-search-container:focus-within {
  border-color: #fbbf24;
  box-shadow: 0 0 0 3px rgba(251, 191, 36, 0.1);
  background: white;
}

.student-dropdown-menu {
  border: 1px solid rgba(251, 191, 36, 0.2);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(251, 191, 36, 0.15);
}

.student-form-select {
  border: 1px solid rgba(251, 191, 36, 0.2);
  border-radius: 8px;
  background: rgba(251, 191, 36, 0.05);
  color: #92400e;
  transition: all 0.3s ease;
}

.student-form-select:focus {
  border-color: #fbbf24;
  box-shadow: 0 0 0 3px rgba(251, 191, 36, 0.1);
  background: white;
}

.student-filter-badge {
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(245, 158, 11, 0.1) 100%);
  border: 1px solid rgba(251, 191, 36, 0.3);
  color: #92400e;
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 0.75rem;
  font-weight: 500;
  transition: all 0.3s ease;
}

.student-filter-badge:hover {
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.2) 0%, rgba(245, 158, 11, 0.15) 100%);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(251, 191, 36, 0.2);
}

.student-table-container {
  border: 1px solid rgba(251, 191, 36, 0.1);
  border-radius: 16px;
  background: linear-gradient(135deg, #ffffff 0%, #fefcf3 100%);
}

.student-status-badge-positive {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
}

.student-status-badge-negative {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border: none;
}
`
    : ""
}
`;

// Create a component to inject the CSS
const StyleInjector = memo(({ isStudentPortal = false }) => {
  return <style>{getStyles(isStudentPortal)}</style>;
});

// Lazy-loaded components for better initial load performance
const LazyMasterFilter = memo(({ showModal, closeModal }) => {
  return <MasterFilter showModal={showModal} closeModal={closeModal} />;
});

// Optimized PaginationNumberInput component
const PaginationNumberInput = memo(
  ({
    currentPage,
    onPageChange,
    totalPages,
    disabled,
    isStudentPortal = false,
  }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [inputValue, setInputValue] = useState(currentPage);
    const [currentValue, setCurrentValue] = useState(currentPage);
    const [showError, setShowError] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
      setCurrentValue(currentPage);
      setInputValue(currentPage);
    }, [currentPage]);

    const inputHandler = useCallback(
      (e) => {
        const value = e.target.value;
        setCurrentValue(value);
        setInputValue(value);

        if (parseInt(value) > totalPages) {
          setShowError(true);
          setTimeout(() => setShowError(false), 2000);
        }
      },
      [totalPages]
    );

    const handleDoubleClick = useCallback(() => {
      if (disabled) return;

      setIsEditing(true);
      setInputValue(currentPage);
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
    }, [disabled, currentPage]);

    const handleBlur = useCallback(() => {
      setIsEditing(false);
      const pageNum = parseInt(inputValue) || 1;

      if (pageNum > totalPages) {
        setCurrentValue(currentPage);
        setShowError(true);
        setTimeout(() => setShowError(false), 2000);
        setInputValue(currentPage);
        return;
      }

      const validPageNum = Math.min(Math.max(1, pageNum), totalPages);
      if (validPageNum !== currentPage) {
        onPageChange(validPageNum);
      }
    }, [inputValue, currentPage, totalPages, onPageChange]);

    const handleKeyPress = useCallback(
      (e) => {
        if (e.key === "Enter") {
          handleBlur();
        }
        if (!/[\d]/.test(e.key) && e.key !== "Enter" && e.key !== "Backspace") {
          e.preventDefault();
        }
      },
      [handleBlur]
    );

    return (
      <OverlayTrigger
        show={showError}
        placement="bottom"
        overlay={
          <Popover
            id="popover-basic"
            className="shadow-sm"
            style={{
              backgroundColor: "#fff",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              border: "1px solid rgba(220,53,69,0.2)",
              borderRadius: "8px",
              padding: "0",
              maxWidth: "200px",
              transform: "translateY(-8px)",
              zIndex: 500,
            }}
          >
            <div
              className="d-flex align-items-center gap-2 p-2 slide-in"
              style={{
                fontSize: "0.75rem",
                color: "#dc3545",
                fontWeight: 500,
                backgroundColor: "rgba(220,53,69,0.05)",
              }}
            >
              <FaExclamationTriangle className="text-danger" />
              <span>Page number exceeds limit</span>
            </div>
          </Popover>
        }
        rootClose
      >
        <Pagination.Item
          active={true}
          className={`p-0 m-0 position-relative ${disabled ? "disabled" : ""}`}
          onDoubleClick={handleDoubleClick}
        >
          <div>
            {isEditing ? (
              <input
                ref={inputRef}
                className={`${
                  isStudentPortal ? "bg-warning" : "bg-primary"
                } text-white border-0 text-center p-0 m-0 ${
                  showError ? "shake-animation" : ""
                }`}
                type="text"
                value={inputValue}
                onChange={inputHandler}
                onBlur={handleBlur}
                onKeyDown={handleKeyPress}
                style={{
                  width: "30px",
                  transition: "all 0.2s ease",
                }}
              />
            ) : (
              <span className={disabled ? "" : "hover-scale"}>
                {currentValue}
              </span>
            )}
          </div>
        </Pagination.Item>
      </OverlayTrigger>
    );
  }
);

// Status Badge Component
const StatusBadge = memo(({ status, field, tab2, isStudentPortal = false }) => {
  const isPositive = status === "Y";

  if (isStudentPortal) {
    return (
      <Badge
        className={`pulse ${
          isPositive
            ? "student-status-badge-positive"
            : "student-status-badge-negative"
        }`}
        style={{
          width: field === "STATUS" ? "80px" : "70px",
          padding: "4px 7px",
          fontSize: "0.75rem",
          fontWeight: "500",
          borderRadius: "8px",
          textTransform: "uppercase",
          letterSpacing: "0.4px",
          transition: "all 0.2s ease",
        }}
      >
        {field === "STATUS"
          ? getMasterActionStatusValue(
              getMasterActionStatusFieldName(tab2),
              status
            )
          : isPositive
          ? "YES"
          : "NO"}
      </Badge>
    );
  }

  return (
    <Badge
      bg=""
      className="pulse"
      style={{
        width: field === "STATUS" ? "80px" : "70px",
        padding: "4px 7px",
        fontSize: "0.75rem",
        fontWeight: "400",
        backgroundColor: isPositive ? "#e8f5e9" : "#ffebee",
        color: isPositive ? "#2e7d32" : "#c62828",
        borderRadius: "4px",
        textTransform: "uppercase",
        letterSpacing: "0.4px",
        transition: "all 0.2s ease",
      }}
    >
      {field === "STATUS"
        ? getMasterActionStatusValue(
            getMasterActionStatusFieldName(tab2),
            status
          )
        : isPositive
        ? "YES"
        : "NO"}
    </Badge>
  );
});

// CellContent component with memoization
const CellContent = memo(
  ({
    value,
    item,
    itemKey,
    handleRowClick,
    groupId,
    groupLevelList,
    groupOwnerId,
    isGroupAdmin,
    tab2,
    isKeyValuePair,
    isStudentPortal = false,
  }) => {
    const getValueForDefault = (value) => {
      if (value === null || value === undefined || value === "") {
        return "-";
      } else {
        return value.toString();
      }
    };

    const isEditable = useCallback(() => {
      if (isGroupAdmin) {
        if (
          item["_USER ID"] === groupOwnerId ||
          item["_USER ID"] === getLocalStorageData("profileDetails")["_ID"]
        ) {
          return false;
        }
        return true;
      }
      return false;
    }, [isGroupAdmin, item, groupOwnerId]);

    const handleCellClick = useCallback(() => {
      handleRowClick(item);
    }, [handleRowClick, item]);

    if (!value || itemKey.startsWith("_")) return null;

    const cellStyle = {
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "400px",
      textAlign:
        itemKey === "NAME" || itemKey === "DESCRIPTION" ? "left" : "center",
      verticalAlign: "middle",
      transition: "background-color 0.15s ease",
    };

    if (itemKey === "STATUS") {
      return (
        <td style={cellStyle} onClick={handleCellClick}>
          <StatusBadge
            status={item[itemKey]}
            field={itemKey}
            tab2={tab2}
            isStudentPortal={isStudentPortal}
          />
        </td>
      );
    }

    if (["SPECIAL", "AC", "AVAILABLE"].includes(itemKey)) {
      return (
        <td style={cellStyle} onClick={handleCellClick}>
          <StatusBadge
            status={item[itemKey]}
            field={itemKey}
            tab2={tab2}
            isStudentPortal={isStudentPortal}
          />
        </td>
      );
    }

    if (itemKey === "NAME" && pagePaths[tab2]?.initialsAvatar) {
      item.isGroupAdmin = isGroupAdmin;
      item.isGroupOwner = item["_USER ID"] === groupOwnerId;
      return (
        <td style={cellStyle}>
          <InitialsAvatar
            onClick={handleCellClick}
            tab2={tab2}
            item={item}
            displayName={item[itemKey]}
          />
        </td>
      );
    }

    if (itemKey === "GROUP LEVEL") {
      return (
        <td className="text-center align-middle">
          <EditableTableCell
            groupLevelList={groupLevelList}
            data={item}
            isEditable={isEditable()}
            groupId={groupId}
          />
        </td>
      );
    }

    if (itemKey === "formResponse") {
      return (
        <td style={cellStyle} onClick={handleCellClick}>
          {isKeyValuePair(item["formResponse"][itemKey])
            ? Object.values(item["formResponse"][itemKey])[0]
            : item["formResponse"][itemKey] ?? "-"}
        </td>
      );
    }

    return (
      <td style={cellStyle} onClick={handleCellClick}>
        {isKeyValuePair(item[itemKey])
          ? Object.values(item[itemKey])[0]
          : getValueForDefault(item[itemKey])}
      </td>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.value === nextProps.value &&
      prevProps.itemKey === nextProps.itemKey &&
      prevProps.item === nextProps.item &&
      prevProps.isStudentPortal === nextProps.isStudentPortal
    );
  }
);

// Table Header Cell component
const TableHeaderCell = memo(
  ({ columnName, handleSortByOnChange, isStudentPortal = false }) => {
    const handleSort = useCallback(() => {
      handleSortByOnChange(columnName);
    }, [columnName, handleSortByOnChange]);

    return (
      <th style={{ verticalAlign: "middle" }}>
        {columnName}{" "}
        <FaSort
          onClick={handleSort}
          style={{
            cursor: "pointer",
            transition: "transform 0.2s ease",
            color: isStudentPortal ? "#92400e" : "inherit",
          }}
          className="hover-scale"
        />
      </th>
    );
  }
);

// Loading Spinner component
const LoadingSpinner = memo(
  ({ size = "md", text = "Loading...", isStudentPortal = false }) => {
    return (
      <div className="d-flex justify-content-center align-items-center py-3 fade-in">
        <Spinner
          animation="border"
          role="status"
          size={size}
          className={`${
            isStudentPortal ? "text-warning" : "text-primary"
          } me-2`}
        />
        <span className="sr-only">{text}</span>
      </div>
    );
  }
);

function getCurrentBaseUrlAndAppend(newPath = "new-form") {
  const baseUrl = `${window.location.protocol}//${window.location.host}`;
  return `${baseUrl}/${newPath}`;
}

const MasterTable = ({
  isAdd = true,
  isFilter = true,
  getTableData,
  addEntry,
  templateId,
  details,
  subjectId,
  type,
  homeworkId,
  classId,
  sectionId,
  tab1,
  roleId,
  tab2,
  tutorId,
  scheduleId,
  groupLevelList,
  deleteRow,
  updateStatus,
  assignmentId,
  AddForm = null,
  externalPermis = null,
  libraryId,
  hostelId,
  roomId,
  groupId,
  departmentId,
  groupOwnerId,
  isGroupAdmin,
  mealId,
  acceptMethod,
  rejectMethod,
  userId,
  isSettings=true,
  menuId,
  actions,
  extraId1,
  entityId,
  formId,
  isStudentPortal = false, // New parameter for student portal theme
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  // **CENTRALIZED COMPONENT PARAMETERS** - Define once, use everywhere
  const componentParams = useMemo(
    () => ({
      // Core identifiers
      extraId1,
      entityId,
      isFilter,
      hostelId,
      tutorId,
      libraryId,
      mealId,
      isSettings, // Default to false, can be overridden
      classId,
      templateId,
      formId,
      scheduleId,
      addEntry,
      homeworkId,
      assignmentId,
      roomId,
      subjectId,
      type,
      sectionId,
      menuId,
      userId,
      groupId,
      departmentId,
      roleId,

      // Tab configuration
      tab1,
      tab2,

      // Component configuration
      isAdd,
      details,
      AddForm,
      externalPermis,
      isStudentPortal, // Add this to component params

      // Group related
      groupLevelList,
      groupOwnerId,
      isGroupAdmin,

      // Methods
      deleteRow,
      updateStatus,
      acceptMethod,
      rejectMethod,
      actions,
      getTableData,

      // Organization data
    }),
    [
      extraId1,
      entityId,
      isFilter,
      hostelId,
      tutorId,
      libraryId,
      mealId,
      classId,
      templateId,
      formId,
      scheduleId,
      homeworkId,
      assignmentId,
      roomId,
      subjectId,
      type,
      sectionId,
      menuId,
      userId,
      groupId,
      departmentId,
      roleId,
      tab1,
      tab2,
      isAdd,
      details,
      AddForm,
      externalPermis,
      isStudentPortal,
      groupLevelList,
      groupOwnerId,
      isGroupAdmin,
      deleteRow,
      updateStatus,
      acceptMethod,
      rejectMethod,
      actions,
      getTableData,
    ]
  );

  // State variables
  const [tableData, setTableData] = useState(null);
  const [methods, setMethods] = useState(
    componentParams.externalPermis || {
      POST: 0,
      GET: 0,
      PUT: 0,
      DELETE: 0,
      PATCH: 0,
    }
  );
  const [tableColumns, setTableColumns] = useState(
    getDefaultColumns(componentParams.tab1, componentParams.tab2)
  );
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortDirection, setSortDirection] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortByValue, setSortByValue] = useState("");
  const[sortingPointer, setSortingPointer] = useState(1)
  // const []
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState({ status: false, message: "" });
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [numberOfEntries, setNumberOfEntries] = useState(10);
  const [defaultColumns, setDefaultColumns] = useState(
    getDefaultColumns(componentParams.tab1, componentParams.tab2)
  );
  const [selectedAcademicYear, setSelectedAcademicYear] = useState(null);
  const [academicYearList, setAcademicYearList] = useState([]);

  // Refs
  const isInitialMount = useRef(true);
  const searchTimeoutRef = useRef(null);
  const isDataFetchingRef = useRef(false);
  const tableRef = useRef(null);
  const lastApiCallRef = useRef(null);

  // Global state
  const { hostelMenuState, toggleHostelMenu } = useGlobalState();
  const [masterTableState, setMasterTableState] = useState(false);
  const { updateMasterTable32wwww } = useGlobalState();
  const { updateAdvanceFilterList } = useGlobalState();
  const [_advanceFilterData, setAdvanceFilterData] = useState(
    filterStorageUtils.getFilterData()
  );
  const [_advanceFilterList, setAdvanceFilterList] = useState(
    filterStorageUtils.getFilterList()
  );

  const resetAdvanceFilterData = useCallback(() => {
    const defaultData = filterStorageUtils.resetFilterData();
    setAdvanceFilterData(defaultData);
  }, []);
  // Memoized function to prepare API params using centralized componentParams
  const getApiParams = useCallback(
    (page = 1, query = null, academicYearId = null) => {
      return {
        extraId1: componentParams.extraId1,
        pageNum: page,
        sortDirection,
        entityId: componentParams.entityId,
        hostelId: componentParams.hostelId,
        tutorId: componentParams.tutorId,
        libraryId: componentParams.libraryId,
        mealId: componentParams.mealId,
        classId: componentParams.classId,
        templateId: componentParams.templateId,
        formId: componentParams.formId,
        scheduleId: componentParams.scheduleId,
        homeworkId: componentParams.homeworkId,
        assignmentId: componentParams.assignmentId,
        roomId: componentParams.roomId,
        subjectId: componentParams.subjectId,
        type: componentParams.type,
        sectionId: componentParams.sectionId,
        menuId: componentParams.menuId,
        userId: componentParams.userId,
        pageSize: numberOfEntries,
        sortBy: sortByValue,
        filterType: _advanceFilterData.date.status
          ? _advanceFilterData.date.type
          : null,
        dateTimeEnd: _advanceFilterData.date.endDate
          ? format(
              new Date(_advanceFilterData.date.endDate),
              "dd/MM/yyyy HH:mm"
            )
          : null,
        dateTimeStart: _advanceFilterData.date.startDate
          ? format(
              new Date(_advanceFilterData.date.startDate),
              "dd/MM/yyyy HH:mm"
            )
          : null,
        status: _advanceFilterData.status.status
          ? _advanceFilterData.status.value
          : null,
        regionId: _advanceFilterData.region.status
          ? _advanceFilterData.region.value
          : null,
        siteId: _advanceFilterData.site.status
          ? _advanceFilterData.site.value
          : null,
        departmentId: _advanceFilterData.department.status
          ? _advanceFilterData.department.value
          : componentParams.departmentId,
        roleId: _advanceFilterData.role.status
          ? _advanceFilterData.role.value
          : componentParams.roleId,
        query: query === null ? searchQuery : query,
        groupId: componentParams.groupId,

        academicYearId: academicYearId || selectedAcademicYear?.value || null,
      };
    },
    [
      componentParams,
      sortDirection,
      numberOfEntries,
      sortByValue,
      _advanceFilterData,
      searchQuery,
      selectedAcademicYear,
    ]
  );

  const getCurrentAcademicYear = useCallback(async () => {
    if (
      componentParams.tab1 === "Examination" &&
      componentParams.tab2 === "Templates"
    ) {
      try {
        const data = await getAcademicYearList();

        if (data.status === "success") {
          if (data.data.entityList && data.data.entityList.length > 0) {
            const academicYearOptions = data.data.entityList.map((item) => ({
              value: item._ID,
              label: item.NAME,
            }));

            setAcademicYearList(academicYearOptions);

            const defaultAcademicYear = {
              value: data.data.entityList[0]._ID,
              label: data.data.entityList[0].NAME,
            };

            if (!selectedAcademicYear) {
              setSelectedAcademicYear(defaultAcademicYear);
            }

            return defaultAcademicYear;
          } else {
            setAcademicYearList([]);
            setSelectedAcademicYear(null);
            return null;
          }
        } else {
          showErrorMessages({ data, navigate });
          setError({ status: true, message: data.message });
          setAcademicYearList([]);
          setSelectedAcademicYear(null);
          return null;
        }
      } catch (error) {
        console.error("Error fetching academic years:", error);
        setAcademicYearList([]);
        setSelectedAcademicYear(null);
        return null;
      }
    }
    return null;
  }, [
    componentParams.tab1,
    componentParams.tab2,
    selectedAcademicYear,
    navigate,
  ]);

  const getThisTableData = useCallback(
    async ({
      page = 1,
      columnRefresh = true,
      paginationLoading = false,
      isLoading = true,
      isSearch = false,
      query = null,
      academicYearId = null,
    } = {}) => {
      console.log("🚀 getThisTableData STARTED with params:", {
        page,
        columnRefresh,
        isLoading,
        isSearch,
        query,
        academicYearId,
      });

      if (isDataFetchingRef.current) {
        console.log("❌ API call already in progress, skipping...");
        return Promise.resolve();
      }

      console.log("✅ No fetch in progress, proceeding...");

      const apiCallId = JSON.stringify({
        page,
        query,
        academicYearId: academicYearId || selectedAcademicYear?.value,
        sortByValue,
        sortDirection,
        numberOfEntries,
        filters: _advanceFilterData,
        timestamp: Date.now(),
      });

      console.log(
        "🔑 Generated API call ID:",
        apiCallId.substring(0, 100) + "..."
      );

      if (lastApiCallRef.current === apiCallId) {
        console.log("❌ Duplicate API call detected, skipping...");
        return Promise.resolve();
      }

      console.log("✅ Not a duplicate call, proceeding...");
      lastApiCallRef.current = apiCallId;
      isDataFetchingRef.current = true;

      console.log("⏳ Setting loading states...");
      if (isLoading) {
        console.log("   - Setting main loading to true");
        setLoading(true);
      }
      if (isSearch) {
        console.log("   - Setting search loading to true");
        setSearchLoading(true);
      }
      if (paginationLoading) {
        console.log("   - Setting pagination loading to true");
        setPaginationLoading(true);
      }

      try {
        let currentAcademicYearId =
          academicYearId || selectedAcademicYear?.value;
        console.log("📅 Academic year ID:", currentAcademicYearId);

        if (
          componentParams.tab1 === "Examination" &&
          componentParams.tab2 === "Templates" &&
          academicYearList.length === 0
        ) {
          console.log("📚 Fetching academic year data...");
          const defaultAcademicYear = await getCurrentAcademicYear();
          if (!currentAcademicYearId && defaultAcademicYear) {
            currentAcademicYearId = defaultAcademicYear.value;
            console.log(
              "📅 Using default academic year:",
              currentAcademicYearId
            );
          }
        }

        console.log("🔧 Preparing API parameters...");
        const apiParams = getApiParams(page, query, currentAcademicYearId);
        console.log("📤 API parameters:", apiParams);

        console.log("🌐 Making API call...");
        const res = await componentParams.getTableData(apiParams);
        console.log("📥 API response received:", {
          status: res.status,
          recordCount: res.data?.totalCount || 0,
          entityListLength: res.data?.entityList?.length || 0,
        });

        if (res.status === "success") {
          console.log("✅ API call successful, processing data...");

          if (componentParams.tab2 === "Forms Response") {
            console.log("🔄 Transforming Forms Response data...");
            if (res.data.entityList && res.data.entityList.length > 0) {
              res.data.entityList.forEach((item, index) => {
                if (item.formResponse) {
                  res.data.entityList[index] = {
                    ...item.formResponse,
                    ...item,
                  };
                  delete res.data.entityList[index].formResponse;
                }
              });
            }
          }

          console.log("💾 Setting table data...");
          setTableData(res.data);

          if (
            componentParams.externalPermis === null ||
            componentParams.externalPermis === undefined
          ) {
            console.log("🔐 Setting methods...");
            setMethods(getMethods(componentParams.tab1, componentParams.tab2));
          }

          if (res.data.entityList.length > 0 && columnRefresh) {
            console.log("📋 Updating table columns...");
            const data = res.data.entityList[0];
            const newTableColumns = { ...tableColumns };

            Object.keys(data).forEach((key) => {
              newTableColumns[key] =
                componentParams.tab2 === "Forms Response" ||
                componentParams.tab1 == "Approval"
                  ? true
                  : defaultColumns.hasOwnProperty(key) || tableColumns[key];
            });

            setTableColumns(newTableColumns);
            console.log("📋 Table columns updated");
          }

          if (
            res.data.entityList.length > 1 &&
            componentParams.tab2 === "Hostel"
          ) {
            console.log("🏨 Toggling hostel menu...");
            toggleHostelMenu(false);
          }

          console.log("📄 Updating pagination states...");
          setCurrentPage(res.data.pageNum);
          setPageSize(res.data.pageSize);
          setTotalCount(res.data.totalCount);

          setError({ status: false, message: "" });

          console.log("🎉 Table data updated successfully!");
          console.log("📊 Final state:", {
            currentPage: res.data.pageNum,
            pageSize: res.data.pageSize,
            totalCount: res.data.totalCount,
          });
        } else {
          console.error("❌ API call failed:", res.message);
          if (sortByValue !== "") {
            setSortByValue("");
          }

          showErrorMessages({ data: res, navigate });
          setError({ status: true, message: res.message });
        }
      } catch (err) {
        console.error("💥 Exception in getThisTableData:", err);
        setError({
          status: true,
          message: "Failed to fetch data. Please try again.",
        });
      } finally {
        console.log("🏁 Finally block - cleaning up loading states...");

        if (isLoading) {
          console.log("   - Setting main loading to false");
          setLoading(false);
        }
        if (paginationLoading) {
          console.log("   - Setting pagination loading to false");
          setPaginationLoading(false);
        }
        if (isSearch) {
          console.log("   - Setting search loading to false");
          setSearchLoading(false);
        }

        console.log("   - Clearing fetch flag");
        isDataFetchingRef.current = false;

        setTimeout(() => {
          lastApiCallRef.current = null;
          console.log("🗑️ Cleared API call reference");
        }, 1000);

        console.log("✨ getThisTableData COMPLETED");
      }
    },
    [
      componentParams,
      getApiParams,
      tableColumns,
      defaultColumns,
      sortByValue,
      toggleHostelMenu,
      navigate,
      getCurrentAcademicYear,
      selectedAcademicYear?.value,
    ]
  );

  const handleAcademicYearChange = useCallback(
    (newValue) => {
      setSelectedAcademicYear(newValue);

      if (getThisTableData && newValue) {
        getThisTableData({
          columnRefresh: false,
          isLoading: false,
          academicYearId: newValue.value,
        });
      }
    },
    [setSelectedAcademicYear, getThisTableData]
  );

  // Updated FilterBadgeComponent with actual values displayed
  const FilterBadgeComponent = memo(({ keyName, isStudentPortal = false }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const tooltipRef = useRef(null);

    const handleRemove = useCallback(() => {
      if (keyName === "region") {
        const currentData = filterStorageUtils.getFilterData();
        const newData = {
          ...currentData,
          [keyName]: {
            ...currentData[keyName],
            status: false,
            label: null,
            value: null,
          },
          ["site"]: {
            ...currentData["site"],
            status: false,
            label: null,
            value: null,
          },
        };
        filterStorageUtils.setFilterData(newData);
      } else {
        const currentData = filterStorageUtils.getFilterData();
        const newData = {
          ...currentData,
          [keyName]: { ...currentData[keyName], status: false },
        };
        filterStorageUtils.setFilterData(newData);
      }

      setTimeout(() => {
        console.log("Filter removed, refreshing table...");
        getThisTableData({ columnRefresh: false, isLoading: false });
      }, 50);
    }, [keyName]);

    // Get the display text based on filter type and value
    const getDisplayText = useCallback(() => {
      const filterData = _advanceFilterData[keyName];

      if (!filterData || !filterData.status) return keyName;

      switch (keyName) {
        case "date":
          const { type, startDate, endDate } = filterData;
          const formatDate = (date) => {
            if (!date) return "";
            const dateObj = new Date(date);
            return format(dateObj, "dd/MM/yy");
          };

          switch (type) {
            case "BETWEEN":
              return `${formatDate(startDate)} - ${formatDate(endDate)}`;
            case "AFTER":
              return `${formatDate(startDate)} →`;
            case "BEFORE":
              return `← ${formatDate(endDate)}`;
            default:
              return "Select Range";
          }

        case "status":
          return filterData.value === "Y" ? "Active" : "Inactive";

        case "region":
        case "site":
        case "department":
        case "role":
          return filterData.label || "Not Selected";

        default:
          return filterData.label || filterData.value || "Not Selected";
      }
    }, [keyName, _advanceFilterData]);

    // Get the full text for tooltip
    const getTooltipText = useCallback(() => {
      const filterData = _advanceFilterData[keyName];

      if (!filterData || !filterData.status) return keyName;

      switch (keyName) {
        case "date":
          const { type, startDate, endDate } = filterData;
          const formatDateTime = (date) => {
            if (!date) return "";
            const dateObj = new Date(date);
            return format(dateObj, "dd/MM/yyyy HH:mm");
          };

          switch (type) {
            case "BETWEEN":
              return `Date Filter: Between ${formatDateTime(
                startDate
              )} and ${formatDateTime(endDate)}`;
            case "AFTER":
              return `Date Filter: From ${formatDateTime(startDate)}`;
            case "BEFORE":
              return `Date Filter: Till ${formatDateTime(endDate)}`;
            default:
              return "Date Filter";
          }

        case "status":
          return `Status Filter: ${
            filterData.value === "Y" ? "Active" : "Inactive"
          }`;

        case "region":
          return `Region Filter: ${filterData.label || "Unknown"}`;
        case "site":
          return `Site Filter: ${filterData.label || "Unknown"}`;
        case "department":
          return `Department Filter: ${filterData.label || "Unknown"}`;
        case "role":
          return `Role Filter: ${filterData.label || "Unknown"}`;

        default:
          return `${keyName} Filter: ${
            filterData.label || filterData.value || "Unknown"
          }`;
      }
    }, [keyName, _advanceFilterData]);

    const displayText = getDisplayText();
    const tooltipText = getTooltipText();

    // Show tooltip if text is longer than 20 characters
    const shouldShowTooltip = displayText.length > 20;

    return (
      <OverlayTrigger
        show={showTooltip && shouldShowTooltip}
        placement="bottom"
        overlay={
          <Popover
            id={`popover-${keyName}`}
            className="shadow-sm fade-in"
            style={{
              backgroundColor: "#fff",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: "12px",
              padding: "0",
              maxWidth: "320px",
              zIndex: 1050,
            }}
          >
            <div
              className="px-3 py-2"
              style={{
                fontSize: "0.8rem",
                color: "#374151",
                fontWeight: 500,
                backgroundColor: "rgba(248,250,252,0.9)",
                borderRadius: "12px",
                lineHeight: 1.4,
              }}
            >
              {tooltipText}
            </div>
          </Popover>
        }
        rootClose
      >
        <div
          ref={tooltipRef}
          className={`d-inline-flex align-items-center ${
            isStudentPortal
              ? "student-filter-badge"
              : "badge bg-primary-subtle text-primary rounded-pill filter-badge"
          }`}
          style={{
            flexShrink: 0,
            cursor: shouldShowTooltip ? "help" : "default",
            maxWidth: "160px",
            minHeight: "38px", // Increased height for two lines
            padding: isStudentPortal ? "6px 12px" : "6px 12px",
            ...(isStudentPortal
              ? {
                  maxWidth: "180px",
                  minHeight: "40px",
                }
              : {
                  border: "1px solid rgba(13, 110, 253, 0.25)",
                  fontSize: "0.72rem",
                  fontWeight: "500",
                  boxShadow: "0 2px 6px rgba(13, 110, 253, 0.08)",
                  background:
                    "linear-gradient(135deg, rgba(13, 110, 253, 0.06) 0%, rgba(13, 110, 253, 0.02) 100%)",
                }),
            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            transform: "translateZ(0)", // Enable hardware acceleration
          }}
          onMouseEnter={() => shouldShowTooltip && setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {/* Two-line layout: Label on top, Value below */}
          <div
            className="d-flex flex-column align-items-center text-center"
            style={{
              lineHeight: 1.1,
              maxWidth: "140px",
            }}
          >
            {/* Filter Label */}
            <span
              className="d-block text-uppercase"
              style={{
                fontSize: "0.6rem",
                fontWeight: "600",
                opacity: 0.8,
                color: isStudentPortal ? "#92400e" : "inherit",
                letterSpacing: "0.5px",
                marginBottom: "1px",
              }}
            >
              {keyName}
            </span>

            {/* Filter Value */}
            <span
              className="d-block"
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "130px",
                fontSize: "0.7rem",
                fontWeight: "500",
                color: isStudentPortal ? "#78350f" : "#0d6efd",
              }}
              title={shouldShowTooltip ? "" : tooltipText}
            >
              {displayText}
            </span>
          </div>

          {/* Close button with enhanced interaction */}
          <button
            type="button"
            className="btn btn-link p-0 ms-2 d-flex align-items-center justify-content-center position-relative"
            onClick={handleRemove}
            style={{
              color: isStudentPortal ? "#92400e" : "#0d6efd",
              opacity: 0.6,
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              minWidth: "18px",
              height: "18px",
              lineHeight: 1,
              fontSize: "0.65rem",
              borderRadius: "50%",
              backgroundColor: "transparent",
              alignSelf: "flex-start",
              marginTop: "2px",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.opacity = "1";
              e.currentTarget.style.transform = "scale(1.15)";
              e.currentTarget.style.backgroundColor = isStudentPortal
                ? "rgba(146, 64, 14, 0.1)"
                : "rgba(13, 110, 253, 0.1)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.opacity = "0.6";
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.backgroundColor = "transparent";
            }}
            aria-label={`Remove ${keyName} filter`}
          >
            <FaTimes />
          </button>
        </div>
      </OverlayTrigger>
    );
  });

  // Initial component mount effect
  useEffect(() => {
    if (isInitialMount.current) {
      dispatch(
        openModal({
          title: componentParams.tab2,
          hostId: componentParams.hostelId,
          data: componentParams.details,
          body: (
            <componentParams.AddForm
              refresh={getThisTableData}
              onSubmit={componentParams.addEntry}
              type={componentParams.type}
              navigate={navigate}
              params={params}
            />
          ),
        })
      );

      const initializeData = async () => {
        if (
          componentParams.tab1 === "Examination" &&
          componentParams.tab2 === "Templates"
        ) {
          await getCurrentAcademicYear();
        }
        await getThisTableData({ columnRefresh: true, isLoading: true });
      };

      initializeData();
      isInitialMount.current = false;

      return () => {
        // Only clear search timeout, DON'T reset filters
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }
  }, [
    dispatch,
    componentParams,
    getThisTableData,
    navigate,
    params,
    getCurrentAcademicYear,
  ]);

  useEffect(() => {
    if (
      !isInitialMount.current &&
      selectedAcademicYear &&
      componentParams.tab1 === "Examination" &&
      componentParams.tab2 === "Templates"
    ) {
      getThisTableData({
        columnRefresh: false,
        isLoading: false,
        academicYearId: selectedAcademicYear.value,
      });
    }
  }, [
    selectedAcademicYear,
    componentParams.tab1,
    componentParams.tab2,
    getThisTableData,
  ]);

  useEffect(() => {
    if (!isInitialMount.current) {
      getThisTableData({ columnRefresh: false });
    }
  }, [sortByValue, numberOfEntries]);

  useEffect(() => {
    if (!isInitialMount.current && masterTableState !== undefined) {
      getThisTableData({ columnRefresh: false, isLoading: false });
    }
  }, [masterTableState, getThisTableData]);

  useEffect(() => {
    const handleFilterChange = () => {
      console.log(
        "🔄 Filter change detected, updating local state and refreshing table..."
      );
      setAdvanceFilterData(filterStorageUtils.getFilterData());
      setAdvanceFilterList(filterStorageUtils.getFilterList());

      // Trigger table refresh after state update
      setTimeout(() => {
        getThisTableData({ columnRefresh: false, isLoading: false });
      }, 100);
    };

    const handleTableRefresh = () => {
      console.log("🔄 Table refresh event received");
      getThisTableData({ columnRefresh: false, isLoading: false });
    };

    // Subscribe to filter changes
    const unsubscribeFilter =
      filterStorageUtils.subscribeToFilterChanges(handleFilterChange);

    // Subscribe to table refresh events
    window.addEventListener("masterTableRefresh", handleTableRefresh);

    return () => {
      unsubscribeFilter();
      window.removeEventListener("masterTableRefresh", handleTableRefresh);
    };
  }, [getThisTableData]);

  const FilterBadges = useMemo(() => {
    return (
      <div className="d-flex flex-wrap gap-1 m-0">
        {Object.entries(_advanceFilterData).map(([key, value], index) =>
          value.status === false ? null : (
            <FilterBadgeComponent
              key={index}
              keyName={key}
              isStudentPortal={componentParams.isStudentPortal}
            />
          )
        )}
      </div>
    );
  }, [_advanceFilterData, componentParams.isStudentPortal]);

  const handleSearch = useCallback(
    (e) => {
      const value = e.target.value.trim();

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      if (!value) {
        setSearchQuery(null);
        getThisTableData({
          columnRefresh: false,
          query: null,
          isLoading: false,
          isSearch: false,
        });
        return;
      }

      if (RegexPatterns.SEARCH_NAME_REGEXP.test(value)) {
        setSearchQuery(value);

        searchTimeoutRef.current = setTimeout(() => {
          getThisTableData({
            columnRefresh: false,
            query: value,
            isLoading: false,
            isSearch: true,
          });
        }, 500);
      }
    },
    [getThisTableData]
  );

  const updateMasterTable = useCallback((value) => {
    setMasterTableState(value);
  }, []);

  const selectAllColumns = useCallback(
    (checked) => {
      setTableColumns((prevState) => {
        const newState = { ...prevState };
        Object.keys(newState).forEach((key) => {
          if (!defaultColumns.hasOwnProperty(key) && !key.startsWith("_")) {
            newState[key] = checked;
          }
        });
        return newState;
      });
    },
    [defaultColumns]
  );

  const toggleColumn = useCallback((key, checked) => {
    setTableColumns((prevState) => ({
      ...prevState,
      [key]: checked,
    }));
  }, []);

  const handleSortByOnChange = useCallback((value) => {
    setSortDirection((prev) => (prev === "ASC" ? "DESC" : "ASC"));
console.log("sortingPointer", sortingPointer)

    if(sortingPointer>=3){
console.log("sortingPointer  resetting sorting pointer")
      setSortingPointer(0)
      setSortByValue(null)
      setSortDirection(null)
    }else{
      console.log("sortingPointer updating sorting pointer");
      setSortingPointer((prev) => prev + 1);
          setSortByValue((prev) => (value));
    }

    

  }, []);

  const handleRowClick = useCallback(
    (item) => {
      const pageConfig = pagePaths[componentParams.tab2]?.viewPage;

      if (pageConfig) {
        dispatch(openModal({ id: item._ID, data: item }));
        const currentPath = location.pathname;
        navigate(`${currentPath}/${pageConfig.label}/${item._ID}`);
      }
    },
    [componentParams.tab2, dispatch, location.pathname, navigate]
  );

  const isKeyValuePair = useCallback((value) => {
    return (
      typeof value === "object" &&
      value !== null &&
      !Array.isArray(value) &&
      Object.keys(value).length > 0
    );
  }, []);

  const handleAddNew = useCallback(() => {
    if (componentParams.tab1 === "Forms" && componentParams.tab2 === "Forms") {
      const newTabUrl = getCurrentBaseUrlAndAppend();
      const newTab = window.open(newTabUrl);

      const handleMessage = (event) => {
        if (event.origin !== window.location.origin) return;

        if (event.data.type === "FORM_SAVED") {
          getThisTableData({ columnRefresh: false, isLoading: false });
          callToast("success", "SAVED", "Form saved successfully!");
          window.removeEventListener("message", handleMessage);
        }
      };

      window.addEventListener("message", handleMessage);

      const checkClosed = setInterval(() => {
        if (newTab.closed) {
          window.removeEventListener("message", handleMessage);
          clearInterval(checkClosed);
        }
      }, 1000);

      return;
    }
    console.log(
      "🔧 Opening modal for adding new item... details: ",
      componentParams.details
    );

    dispatch(
      openModal({
        isOpen: true,
        title: componentParams.tab2,
        data: componentParams.details,
        body: (
          <componentParams.AddForm
            refresh={getThisTableData}
            onSubmit={componentParams.addEntry}
            type={componentParams.type}
            navigate={navigate}
            params={params}
            details={componentParams.details}
          />
        ),
        isEditing: false,
      })
    );
  }, [componentParams, dispatch, getThisTableData, navigate, params]);

  // REPLACE the existing refreshTable function with this:
  const refreshTable = useCallback(() => {
    console.log("🔄 MANUAL REFRESH TRIGGERED");
    console.log("📋 Current state before refresh:", {
      masterTableState,
      currentPage,
      searchQuery,
      sortByValue,
      totalCount,
    });

    try {
      console.log("🧹 Resetting advance filter data...");
      resetAdvanceFilterData();

      console.log("⏱️ Clearing search timeout...");
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      console.log("🔍 Resetting search query...");
      setSearchQuery("");

      console.log("📄 Resetting to first page...");
      setCurrentPage(1);

      console.log("⏳ Scheduling data fetch...");
      setTimeout(() => {
        console.log("🚀 Executing scheduled table data refresh...");
        getThisTableData({
          columnRefresh: true,
          isLoading: false,
          page: 1,
          query: null,
        })
          .then(() => {
            console.log("✅ Manual refresh completed successfully");
          })
          .catch((error) => {
            console.error("❌ Manual refresh failed:", error);
          });
      }, 100);
    } catch (error) {
      console.error("💥 Error during manual refresh:", error);
    }
  }, [
    resetAdvanceFilterData,
    getThisTableData,
    masterTableState,
    currentPage,
    searchQuery,
    sortByValue,
    totalCount,
  ]);

  const debugRefresh = useCallback(() => {
    console.log("=== DEBUG REFRESH ===");
    console.log("Current masterTableState:", masterTableState);
    console.log("Current _advanceFilterData:", _advanceFilterData);
    console.log("Current page:", currentPage);
    console.log("Is initial mount:", isInitialMount.current);
    console.log("Is data fetching:", isDataFetchingRef.current);
    console.log("Last API call ID:", lastApiCallRef.current);
    console.log("Search query:", searchQuery);
    console.log("Sort by value:", sortByValue);
    console.log("Sort direction:", sortDirection);
    console.log("Number of entries:", numberOfEntries);
    console.log("Selected academic year:", selectedAcademicYear);
    console.log("Is student portal:", componentParams.isStudentPortal);

    console.log("getThisTableData function exists:", typeof getThisTableData);

    console.log("=== CALLING getThisTableData ===");
    getThisTableData({
      columnRefresh: false,
      isLoading: false,
      page: 1,
    })
      .then(() => {
        console.log("=== getThisTableData completed ===");
      })
      .catch((error) => {
        console.error("=== getThisTableData failed ===", error);
      });
  }, [
    masterTableState,
    _advanceFilterData,
    currentPage,
    searchQuery,
    sortByValue,
    sortDirection,
    numberOfEntries,
    selectedAcademicYear,
    componentParams.isStudentPortal,
    getThisTableData,
  ]);

  const handleEntriesChange = useCallback((e) => {
    const newSize = Number(e.target.value);
    setNumberOfEntries(newSize);
  }, []);

  const handlePrevPage = useCallback(() => {
    getThisTableData({
      page: Math.max(currentPage - 1, 1),
      columnRefresh: false,
      isLoading: false,
      paginationLoading: true,
    });
  }, [currentPage, getThisTableData]);

  const handleNextPage = useCallback(() => {
    const maxPage = Math.ceil(totalCount / pageSize);
    getThisTableData({
      page: Math.min(currentPage + 1, maxPage),
      columnRefresh: false,
      isLoading: false,
      paginationLoading: true,
    });
  }, [currentPage, totalCount, pageSize, getThisTableData]);

  const selectAllState = useMemo(() => {
    const selectableColumns = Object.entries(tableColumns).filter(
      ([key, value]) =>
        !defaultColumns.hasOwnProperty(key) && !key.startsWith("_")
    );

    if (selectableColumns.length === 0) {
      return { allSelected: false, someSelected: false };
    }

    const selectedCount = selectableColumns.filter(
      ([key, value]) => value
    ).length;

    return {
      allSelected: selectedCount === selectableColumns.length,
      someSelected:
        selectedCount > 0 && selectedCount < selectableColumns.length,
    };
  }, [tableColumns, defaultColumns]);

  // Memoized table header with student portal theme
  const TableHeader = useMemo(() => {
    return (
      <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center mb-3 gap-2 slide-in">
        <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2 flex-grow-1">
          <h4
            className="mb-1 mb-sm-0"
            style={{
              color: componentParams.isStudentPortal ? "#92400e" : "inherit",
            }}
          >
            {componentParams.tab2}
          </h4>
          {totalCount > 0 && (
            <Badge
              className={`align-self-start align-self-sm-center ${
                componentParams.isStudentPortal ? "student-badge" : ""
              }`}
              bg={componentParams.isStudentPortal ? "" : "primary"}
              style={{
                fontSize: "0.75rem",
                fontWeight: "500",
                ...(componentParams.isStudentPortal
                  ? {}
                  : {
                      padding: "4px 8px",
                      borderRadius: "12px",
                    }),
              }}
            >
              {totalCount}
            </Badge>
          )}
        </div>

        <div
          className="d-flex flex-column flex-md-row align-items-end justify-content-end gap-2"
          style={{ flexShrink: 0, width: "auto" }}
        >
          <div
            className="d-flex flex-nowrap justify-content-end align-items-center gap-2"
            style={{ flexShrink: 0 }}
          >
            {componentParams.tab1 === "Examination" &&
              componentParams.tab2 === "Templates" && (
                <AcademicYearSelect
                  academicYearList={academicYearList}
                  selectedAcademicYear={selectedAcademicYear}
                  setSelectedAcademicYear={setSelectedAcademicYear}
                  getThisTableData={getThisTableData}
                />
              )}
            {methods.POST === 0 ? null : componentParams.isAdd == true ? (
              <Button
                variant={componentParams.isStudentPortal ? "" : "primary"}
                className={`${
                  componentParams.isStudentPortal ? "student-btn-primary" : ""
                } hover-scale`}
                size="sm"
                onClick={handleAddNew}
              >
                Add
              </Button>
            ) : null}
            {componentParams.isSettings == true ? (
              <Dropdown autoClose="outside">
                <Dropdown.Toggle
                  variant={componentParams.isStudentPortal ? "" : "light"}
                  className={`${
                    componentParams.isStudentPortal ? "student-btn-light" : ""
                  } hover-scale`}
                  size="sm"
                >
                  <FaCog className="text-secondary" />
                </Dropdown.Toggle>
                <Dropdown.Menu
                  className={`p-2 shadow-sm fade-in ${
                    componentParams.isStudentPortal
                      ? "student-dropdown-menu"
                      : ""
                  }`}
                >
                  <div className="px-2 py-1">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="selectAll"
                        checked={selectAllState.allSelected}
                        ref={(input) => {
                          if (input)
                            input.indeterminate = selectAllState.someSelected;
                        }}
                        onChange={(e) => {
                          e.stopPropagation();
                          selectAllColumns(e.target.checked);
                        }}
                      />
                      <label
                        className="form-check-label ms-2"
                        htmlFor="selectAll"
                      >
                        SELECT ALL
                      </label>
                    </div>
                  </div>
                  {tableColumns &&
                    Object.entries(tableColumns).map(([key, value], index) => {
                      if (key[0] === "_") return null;
                      return (
                        <div
                          key={key}
                          className="px-2 py-1 dropdown-menu-item border-bottom hover-scale"
                        >
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={`checkbox-${key}`}
                              checked={tableColumns[key]}
                              disabled={defaultColumns.hasOwnProperty(key)}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleColumn(key, e.target.checked);
                              }}
                            />
                            <label
                              className="form-check-label ms-2"
                              htmlFor={`checkbox-${key}`}
                            >
                              {key}
                            </label>
                          </div>
                        </div>
                      );
                    })}
                </Dropdown.Menu>
              </Dropdown>
            ) : null}

            {componentParams.isFilter == true ? (
              <Button
                variant={componentParams.isStudentPortal ? "" : "light"}
                className={`${
                  componentParams.isStudentPortal ? "student-btn-light" : ""
                } hover-scale`}
                size="sm"
                onClick={() => setShowFilterModal(true)}
              >
                <FaFilter className="text-secondary" />
              </Button>
            ) : null}

            <Button
              variant={componentParams.isStudentPortal ? "" : "light"}
              className={`${
                componentParams.isStudentPortal ? "student-btn-light" : ""
              } hover-scale`}
              size="sm"
              onClick={debugRefresh}
            >
              <FaRedo />
            </Button>
          </div>
          <InputGroup
            className={`${
              componentParams.isStudentPortal ? "student-search-container" : ""
            }`}
            style={{
              width: "250px",
              maxWidth: "250px",
              flexShrink: 0,
              ...(componentParams.isStudentPortal
                ? {}
                : {
                    border: "1px solid #dee2e6",
                    borderRadius: "0.375rem",
                    overflow: "hidden",
                  }),
              transition: "all 0.2s ease",
            }}
          >
            <InputGroup.Text
              className={`bg-transparent border-0 ${
                searchLoading ? "pe-2" : "pe-1"
              }`}
              style={{
                transition: "all 0.2s ease",
              }}
            >
              {searchLoading ? (
                <Spinner
                  animation="border"
                  role="status"
                  size="sm"
                  className={`${
                    componentParams.isStudentPortal
                      ? "text-warning"
                      : "text-primary"
                  }`}
                  style={{ width: "1.2rem", height: "1.2rem" }}
                />
              ) : (
                <FiSearch className="text-muted" />
              )}
            </InputGroup.Text>

            <Form.Control
              size="sm"
              type="text"
              placeholder="Search..."
              value={searchQuery ?? ""}
              onChange={handleSearch}
              className="border-0 ps-1"
              style={{
                transition: "all 0.2s ease",
                outline: "none",
                border: "none !important",
                boxShadow: "none",
              }}
              onFocus={(e) => {
                if (componentParams.isStudentPortal) {
                  e.target.parentElement.style.borderColor = "#fbbf24";
                  e.target.parentElement.style.boxShadow =
                    "0 0 0 3px rgba(251, 191, 36, 0.1)";
                } else {
                  e.target.parentElement.style.borderColor = "#86b7fe";
                  e.target.parentElement.style.boxShadow =
                    "0 0 0 0.2rem rgba(13, 110, 253, 0.25)";
                }
              }}
              onBlur={(e) => {
                if (componentParams.isStudentPortal) {
                  e.target.parentElement.style.borderColor =
                    "rgba(251, 191, 36, 0.2)";
                  e.target.parentElement.style.boxShadow = "none";
                } else {
                  e.target.parentElement.style.borderColor = "#dee2e6";
                  e.target.parentElement.style.boxShadow = "none";
                }
              }}
            />
          </InputGroup>
        </div>
      </div>
    );
  }, [
    componentParams,
    totalCount,
    academicYearList,
    selectedAcademicYear,
    methods,
    selectAllState,
    tableColumns,
    defaultColumns,
    searchLoading,
    searchQuery,
    handleAddNew,
    selectAllColumns,
    toggleColumn,
    handleSearch,
    refreshTable,
    getThisTableData,
  ]);

  // Memoized table body with student portal theme
  const TableBody = useMemo(() => {
    if (
      !tableData ||
      !tableData.entityList ||
      tableData.entityList.length === 0
    ) {
      return <></>;
    }

    return (
      <tbody>
        {tableData.entityList.map((item, index) => (
          <tr
            className="table-row"
            style={{
              maxHeight: "40px",
              animation: `fadeIn 0.3s ease-in-out ${index * 0.05}s both`,
            }}
            key={index}
          >
            {Object.entries(tableColumns).map(([key, value]) => (
              <CellContent
                key={key}
                item={item}
                itemKey={key}
                groupId={componentParams.groupId}
                groupOwnerId={componentParams.groupOwnerId}
                groupLevelList={componentParams.groupLevelList}
                tab2={componentParams.tab2}
                value={value}
                isGroupAdmin={componentParams.isGroupAdmin}
                handleRowClick={handleRowClick}
                isKeyValuePair={isKeyValuePair}
                isStudentPortal={componentParams.isStudentPortal}
              />
            ))}
            <td style={{ textAlign: "center" }}>
              <MasterActions
                refresh={getThisTableData}
                deleteRow={componentParams.deleteRow}
                publish={getMasterActionStatusFieldName(componentParams.tab2)}
                acceptMethod={componentParams.acceptMethod}
                rejectMethod={componentParams.rejectMethod}
                updateStatus={componentParams.updateStatus}
                permission={componentParams.details?._PERMISSION}
                actions={componentParams.actions}
                AddForm={
                  <componentParams.AddForm
                    refresh={getThisTableData}
                    onSubmit={componentParams.addEntry}
                    type={componentParams.type}
                    navigate={navigate}
                    details={componentParams.details}
                    params={params}
                  />
                }
                methods={methods}
                item={item}
                title={componentParams.tab2}
              />
            </td>
          </tr>
        ))}
      </tbody>
    );
  }, [
    tableData,
    tableColumns,
    componentParams,
    handleRowClick,
    isKeyValuePair,
    getThisTableData,
    methods,
    navigate,
    params,
  ]);

  // Memoized pagination section with student portal theme
  const PaginationSection = useMemo(() => {
    if (totalCount === 0) return null;

    return (
      <div className="d-flex justify-content-center justify-content-md-end flex-grow-1 align-items-center">
        <div className="d-flex align-items-center">
          {paginationLoading && (
            <div className="me-2">
              <div
                className={`spinner-border spinner-border-sm ${
                  componentParams.isStudentPortal
                    ? "text-warning"
                    : "text-primary"
                }`}
                role="status"
              >
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}

          <Pagination
            className={`mb-0 flex-wrap ${
              componentParams.isStudentPortal ? "student-pagination" : ""
            }`}
          >
            <Pagination.Prev
              onClick={handlePrevPage}
              disabled={currentPage === 1 || paginationLoading}
              className="hover-scale"
            />
            <PaginationNumberInput
              currentPage={currentPage}
              onPageChange={async (page) => {
                getThisTableData({
                  page,
                  isLoading: false,
                  columnRefresh: false,
                  paginationLoading: true,
                });
              }}
              totalPages={Math.ceil(totalCount / pageSize)}
              disabled={paginationLoading}
              isStudentPortal={componentParams.isStudentPortal}
            />
            <Pagination.Next
              className="ms-0 ps-0 hover-scale"
              onClick={handleNextPage}
              disabled={
                currentPage === Math.ceil(totalCount / pageSize) ||
                paginationLoading
              }
            />
          </Pagination>
        </div>
      </div>
    );
  }, [
    totalCount,
    paginationLoading,
    currentPage,
    pageSize,
    handlePrevPage,
    handleNextPage,
    getThisTableData,
    componentParams.isStudentPortal,
  ]);

  // Memoized entries info
  const EntriesInfo = useMemo(() => {
    if (totalCount === 0) {
      return loading ? "Loading entries..." : null;
    }

    return (
      <>
        page {currentPage} to {Math.ceil(totalCount / pageSize)} ({" "}
        {(currentPage - 1) * pageSize + 1} to{" "}
        {Math.min(currentPage * pageSize, totalCount)} of {totalCount} entries)
      </>
    );
  }, [totalCount, loading, currentPage, pageSize]);

  return (
    <>
      <StyleInjector isStudentPortal={componentParams.isStudentPortal} />

      <Suspense fallback={<div>Loading...</div>}>
        <LazyMasterFilter
          showModal={showFilterModal}
          closeModal={() => setShowFilterModal(false)}
        />
      </Suspense>

      {error.status ? (
        <center className="fade-in">{error.message}</center>
      ) : (
        <Row className="fade-in">
          <Col sm="12">
            <Card
              className={`shadow-sm m-0 p-0 ${
                componentParams.isStudentPortal ? "student-card" : ""
              }`}
            >
              <Card.Header
                className={`pb-3 ${
                  componentParams.isStudentPortal ? "student-card-header" : ""
                }`}
              >
                {TableHeader}
                {FilterBadges}
              </Card.Header>
              <Card.Body className="pt-0 mt-0">
                {loading ? (
                  <LoadingSpinner
                    isStudentPortal={componentParams.isStudentPortal}
                  />
                ) : error.status ? (
                  <center className="fade-in">
                    <h3>{error.message}</h3>
                  </center>
                ) : (
                  <div
                    ref={tableRef}
                    className={`table-responsive border table-container fade-in ${
                      componentParams.isStudentPortal
                        ? "student-table-container"
                        : ""
                    }`}
                    style={{
                      minHeight: "70vh",
                      maxHeight: "70vh",
                      overflow: "auto",
                      overflowX: "visible",
                      borderRadius: "15px",
                      boxShadow: componentParams.isStudentPortal
                        ? "0 4px 6px -1px rgba(251, 191, 36, 0.1), 0 2px 4px -1px rgba(245, 158, 11, 0.06)"
                        : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    }}
                  >
                    <Table
                      hover
                      className="mb-0 table-fixed"
                      style={{
                        position: "relative",
                        borderCollapse: "separate",
                        borderSpacing: 0,
                        overflow: "auto",
                        borderRadius: "15px",
                      }}
                    >
                      <thead
                        className={
                          componentParams.isStudentPortal
                            ? "student-table-header"
                            : ""
                        }
                        style={{
                          position: "sticky",
                          top: 0,
                          background: componentParams.isStudentPortal
                            ? "linear-gradient(135deg, rgba(251, 191, 36, 0.05) 0%, rgba(245, 158, 11, 0.03) 100%)"
                            : "white",
                          zIndex: 2,
                          boxShadow: componentParams.isStudentPortal
                            ? "0 2px 2px -1px rgba(251, 191, 36, 0.1)"
                            : "0 2px 2px -1px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        <tr
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "400px",
                            textAlign: "center",
                            verticalAlign: "middle",
                          }}
                        >
                          {Object.entries(tableColumns).map(
                            ([key, value], index) =>
                              !value ? null : key[0] === "_" ? null : (
                                <TableHeaderCell
                                  key={key}
                                  columnName={key}
                                  handleSortByOnChange={handleSortByOnChange}
                                  isStudentPortal={
                                    componentParams.isStudentPortal
                                  }
                                />
                              )
                          )}
                          <th
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "400px",
                              textAlign: "center",
                              verticalAlign: "middle",
                              color: componentParams.isStudentPortal
                                ? "#92400e"
                                : "inherit",
                              fontWeight: componentParams.isStudentPortal
                                ? "600"
                                : "inherit",
                              fontSize: componentParams.isStudentPortal
                                ? "0.875rem"
                                : "inherit",
                              textTransform: componentParams.isStudentPortal
                                ? "uppercase"
                                : "inherit",
                              letterSpacing: componentParams.isStudentPortal
                                ? "0.5px"
                                : "inherit",
                            }}
                          >
                            ACTION
                          </th>
                        </tr>
                      </thead>
                      {TableBody}
                    </Table>
                    {tableData && !tableData.entityList.length && (
                      <center>
                        <p className="text-center p-3">No records found.</p>
                      </center>
                    )}
                  </div>
                )}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-stretch mt-3 gap-2">
                  <div className="d-flex justify-content-between justify-content-md-start align-items-center gap-3 flex-grow-1">
                    <div
                      className="text-nowrap"
                      style={{
                        color: componentParams.isStudentPortal
                          ? "#92400e"
                          : "inherit",
                        fontSize: componentParams.isStudentPortal
                          ? "0.875rem"
                          : "inherit",
                      }}
                    >
                      {EntriesInfo}
                    </div>

                    {totalCount > 0 && (
                      <div className="flex-shrink-0">
                        <select
                          className={`form-select form-select-sm hover-scale ${
                            componentParams.isStudentPortal
                              ? "student-form-select"
                              : ""
                          }`}
                          style={{
                            padding: "0.25rem 1.5rem 0.25rem 0.5rem",
                            backgroundSize: "12px 12px",
                            backgroundPosition: "right 0.35rem center",
                            transition: "all 0.2s ease",
                          }}
                          value={numberOfEntries}
                          onChange={handleEntriesChange}
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>
                    )}
                  </div>

                  {PaginationSection}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
};

export default memo(MasterTable);
